import type { PreviousUnderwrite, UnderwriteSummary } from '../api.js';
import { timestamp } from '../format.js';
import { AnalyzerLink } from './AnalyzerLink.js';

/**
 * HUB-3 — "what's the latest / what changed", answered on the RECORD (R9's fifth verb on the Hub).
 *
 * The report has answered "what changed since the previous underwrite" since R1 (#767), but only once a
 * reader had found the run — and the project header offered no route to it. This states the two
 * headline moves (recommendation and score) where the reader already stands, and routes to the latest
 * run, whose `ReportChanges` carries the full comparison.
 *
 * ⚠️ The baseline comes from the SAME lookup the report uses (`findPreviousUnderwrite`). The one
 * difference is the anchor: the Hub correlates on the record's code, the report on the run's own
 * code (`input.project.code` or `input.projectCode` — the api's `runProjectCode`). They coincide for a numbered record's runs; for a promoted pursuit's older,
 * uncoded runs both are silent.
 *
 * ⚠️ SILENT WHEN THERE IS NO BASELINE. The api omits `previous` for a first underwrite, a pursuit (no
 * code to correlate on) AND a latest run that did not succeed. Those are different states, and this
 * component cannot tell them apart — so it claims none of them rather than guessing "first".
 */
export function RecordChanges({
  previous,
  latest,
  latestAnalysisId,
}: {
  previous?: PreviousUnderwrite;
  latest: UnderwriteSummary | null;
  latestAnalysisId?: string | null;
}) {
  if (!previous || !latestAnalysisId || !latest) return null;

  const when = previous.completedAt ? ` on ${timestamp(previous.completedAt)}` : '';
  /*
    🔴 A LINK THROUGH `AnalyzerLink`, NOT `navigate` (#1399). This renders on the project page, which
    the Hub shares; the Hub has no `/analyses` route, and an in-app navigate there was redirected to
    `/projects` — the reader asked for the comparison and got the project list.
  */
  const open = (
    <AnalyzerLink className="link-btn" to={`/analyses/${encodeURIComponent(latestAnalysisId)}`}>
      Open the latest underwrite for the full comparison →
    </AnalyzerLink>
  );
  /*
    🔴 A predecessor that never UNDERWROTE — a thin package filed `needs_more_docs`, stored as a
    succeeded run with no scorecard. It is the most common way a new record's first real underwrite
    gets a "previous": calling it "the previous underwrite" and listing two "not comparable" pairs
    would tell the reader this record was underwritten twice. The report says this in one sentence
    (`producedNoUnderwrite` in ReportChanges); so does the Hub.
  */
  if (previous.recommendation === null && previous.overallScore === null) {
    return (
      <p className="report-changes">
        The run before this one{when} did not produce an underwrite, so there is nothing to compare
        against. {open}
      </p>
    );
  }
  const moves = [
    move('Recommendation', previous.recommendation, latest.verdict ?? null),
    move('Score', previous.overallScore, latest.overallScore ?? null),
  ];

  return (
    <p className="report-changes">
      Since the previous underwrite{when}: {moves.join(' · ')}. {open}
    </p>
  );
}

/**
 * One headline move, stated honestly. A side that is missing makes the pair NOT COMPARABLE — never
 * "unchanged", which would be a false all-clear about a figure one run never stated.
 */
export function move(
  label: string,
  before: string | number | null,
  after: string | number | null,
): string {
  if (before === null || after === null) return `${label.toLowerCase()} not comparable`;
  if (before === after) return `${label.toLowerCase()} unchanged (${String(after)})`;
  return `${label.toLowerCase()} ${String(before)} → ${String(after)}`;
}
